import { injectable } from 'tsyringe'

import { ManageCouriers } from '@/domain/entities/ManageCourier'
import { DashboardPickupTransferOutcome, NearbyCourier, PickupAdditionalTimes, PickupCorporates, PickupCorporateSummary, PickupCRRNFDSummary, PickupCRROTWV2List, PickupCRROTWV2Summary, PickupCRRQUEs, PickupDetail, PickupDetailSnapshot, PickupForceDetail, PickupNfdList, PickupNfdShipperHashDetail, PickupNfdSummary, PickupShipperHashOutcome, Pickups, PickupSummary, PickupCRROTWs, AssignPickupBulkHistoryList, AdminContactShow, PickupCorporatesV2, PickupCorporateNfdDeadlineSummary  } from '@/domain/entities/Pickup'
import {
  AdminTaskPickupCancelRequestInterface,
  AdminTaskPickupDoneRequestInterface,
  CancelPickupRequestInterface,
  ExportPickupRequestInterface,
  ManualAssigneeBulkRequestInterface,
  ManualAssigneeRequestInterface,
  MarkAsDoneRequestInterface,
  PickupDedicatedCRROTWRequestInterface,
  PickupForceCRRCNCRequestInterface,
  ReadAssignPickupBulkHistoryRequestInterface,
} from '@/data/payload/contracts/PickupRequest'
import { DashboardPickupTransferRequest, ExportPickupRequest, ForceCancelCRRQUERequest, ForceCRRSRCRequest, UpdateIsContactedCRROTWRequest, UpdateIsContactedCRRQUERequest } from '@/data/payload/api/PickupRequest'
import { Report } from '@/domain/entities/Main'
import { PickupApiRepository } from '@/app/infrastructures/repositories/api/PickupApiRepository'

@injectable()
export class PickupPresenter {
  private repository: PickupApiRepository

  constructor(repository: PickupApiRepository) {
    this.repository = repository
  }

  public getAll(params: Map<string, string | number>): Promise<Pickups> {
    return this.repository.getAll(params)
  }

  public get(params: { shipmentId: string; isAllData?: boolean }): Promise<PickupDetail> {
    return this.repository.get(params)
  }

  public getBulk(params: Map<string, string | number>): Promise<PickupDetail[]> {
    return this.repository.getBulk(params)
  }

  public getPickupCRRNFD(params: Map<string, string | number>): Promise<Pickups> {
    return this.repository.getListCRRNFD(params)
  }

  public getPickupNfdList(
    params: Map<string, string | number>
  ): Promise<PickupNfdList> {
    return this.repository.getPickupNfdList(params)
  }

  public getPickupNfdSummary(
    params: Map<string, string | number>
  ): Promise<PickupNfdSummary> {
    return this.repository.getPickupNfdSummary(params)
  }

  public getPickupNfdByShipperHash(
    shipperHash: string,
    params: Map<string, string | number>
  ): Promise<PickupNfdShipperHashDetail> {
    return this.repository.getPickupNfdByShipperHash(shipperHash, params)
  }

  public postPickupNfdShipperHashToCrrsrc(
    shipperHashes: string[]
  ): Promise<PickupShipperHashOutcome[]> {
    return this.repository.postPickupNfdShipperHashToCrrsrc(shipperHashes)
  }

  public manualAssignPickupBulkV2(
    payload: ManualAssigneeBulkRequestInterface
  ): Promise<PickupShipperHashOutcome[]> {
    return this.repository.manualAssignPickupBulkV2(payload)
  }

  public getAssignPickupBulkHistory(
    params: Map<string, string | number>
  ): Promise<AssignPickupBulkHistoryList> {
    return this.repository.getAssignPickupBulkHistory(params)
  }

  public readAssignPickupBulkHistory(
    payload: ReadAssignPickupBulkHistoryRequestInterface
  ): Promise<boolean> {
    return this.repository.readAssignPickupBulkHistory(payload)
  }

  public getPickupCRRDON(params: Map<string, string | number>): Promise<Pickups> {
    return this.repository.getListCRRDON(params)
  }

  public getPickupDetail(params: Map<string, string | number>): Promise<PickupForceDetail> {
    return this.repository.getPickupDetail(params)
  }

  public getSnapshot(shipmentId: string): Promise<PickupDetailSnapshot[]> {
    return this.repository.getSnapshot(shipmentId)
  }

  public getSummary(params: Map<string, string>): Promise<PickupSummary> {
    return this.repository.getSummary(params)
  }

  public export(payload: ExportPickupRequestInterface): Promise<string[]> {
    return this.repository.export(payload)
  }

  public exportCRRNFD(params: Map<string, string>): Promise<string> {
    return this.repository.exportCRRNFD(params)
  }

  public getNearbyCourier(params: Map<string, number>): Promise<NearbyCourier[]> {
    return this.repository.getNearbyCourier(params)
  }

  public manualAssignee(
    payload: ManualAssigneeRequestInterface
  ): Promise<any> {
    return this.repository.manualAssignee(payload)
  }

  public markAsDone(
    payload: MarkAsDoneRequestInterface,
    group: boolean
  ): Promise<boolean> {
    return this.repository.markAsDone(payload, group)
  }

  public adminTaskPickupDone(
    payload: AdminTaskPickupDoneRequestInterface
  ): Promise<boolean> {
    return this.repository.adminTaskPickupDone(payload)
  }

  public adminTaskPickupCancel(
    payload: AdminTaskPickupCancelRequestInterface
  ): Promise<boolean> {
    return this.repository.adminTaskPickupCancel(payload)
  }

  public cancelPickup(payload: CancelPickupRequestInterface): Promise<boolean> {
    return this.repository.cancelPickup(payload)
  }

  public pickupDedicatedCRROTW(payload: PickupDedicatedCRROTWRequestInterface): Promise<boolean> {
    return this.repository.pickupDedicatedCRROTW(payload)
  }

  public getPickupAdditionalTime(params: Map<string, string>): Promise<PickupAdditionalTimes> {
    return this.repository.getPickupAdditionalTime(params)
  }

  public exportPickupList(params: Map<string, string>): Promise<string> {
    return this.repository.exportPickupList(params)
  }

  public getListCRROTW(params: Map<string, string>): Promise<PickupCRROTWs> {
    return this.repository.getListCRROTW(params)
  }

  public getListCRROTWV2(
    params: Map<string, string | number>
  ): Promise<PickupCRROTWV2List> {
    return this.repository.getListCRROTWV2(params)
  }

  public getSummaryCRROTWV2(
    params: Map<string, string | number>
  ): Promise<PickupCRROTWV2Summary> {
    return this.repository.getSummaryCRROTWV2(params)
  }

  public forceCRRCNC(payload: PickupForceCRRCNCRequestInterface): Promise<boolean> {
    return this.repository.forceCRRCNC(payload)
  }

  public updateIsContactedCRROTW(taskId: number, payload: UpdateIsContactedCRROTWRequest): Promise<boolean> {
    return this.repository.updateIsContactedCRROTW(taskId, payload)
  }

  public exportCRROTW(params: Map<string, string>): Promise<string> {
    return this.repository.exportCRROTW(params)
  }

  public forceCRRSRC(payload: ForceCRRSRCRequest): Promise<boolean> {
    return this.repository.forceCRRSRC(payload)
  }

  public getListCRRQUE(params: Map<string, string | number>): Promise<PickupCRRQUEs> {
    return this.repository.getListCRRQUE(params)
  }

  public forceCancelCRRQUE(payload: ForceCancelCRRQUERequest): Promise<boolean> {
    return this.repository.forceCancelCRRQUE(payload)
  }

  public updateIsContactedCRRQUE(shipmentId: string, payload: UpdateIsContactedCRRQUERequest): Promise<boolean> {
    return this.repository.updateIsContactedCRRQUE(shipmentId, payload)
  }

  public exportCRRQUE(params: Map<string ,string | number>): Promise<string> {
    return this.repository.exportCRRQUE(params)
  }

  public getCRRNFDSummary(): Promise<PickupCRRNFDSummary> {
    return this.repository.getCRRNFDSummary()
  }

  public exportManualAssignLog(params: Map<string, string>): Promise<string> {
    return this.repository.exportManualAssignLog(params)
  }

  public getListCorporate(params: Map<string, string | number>): Promise<PickupCorporates> {
    return this.repository.getListCorporate(params);
  }

  public getListCorporateV2(params: Map<string, string | number>): Promise<PickupCorporatesV2> {
    return this.repository.getListCorporateV2(params);
  }

  public getCorporateSummary(): Promise<PickupCorporateSummary> {
    return this.repository.getCorporateSummary();
  }

  public getExportPickup(payload: ExportPickupRequest): Promise<Report> {
    return this.repository.getExportPickup(payload)
  }

  public getCouriersForDashboardPickupTransfer(
    params: Map<string, string | number>
  ): Promise<ManageCouriers> {
    return this.repository.getCouriersForDashboardPickupTransfer(params)
  }

  public transferDashboardPickup(
    payload: DashboardPickupTransferRequest
  ): Promise<DashboardPickupTransferOutcome[]> {
    return this.repository.transferDashboardPickup(payload)
  }

  public adminContactShow(
    params: Map<string, string>
  ): Promise<AdminContactShow> {
    return this.repository.adminContactShow(params)
  }

  public syncPickupCorporate() {
    return this.repository.syncPickupCorporate();
  }

  public getNfdDeadlineSummary(params: Map<string, string | number>): Promise<PickupCorporateNfdDeadlineSummary> {
    return this.repository.getNfdDeadlineSummary(params);
  }

  public exportCorporate(params: Map<string, string | number | boolean>): Promise<Report> {
    return this.repository.exportCorporate(params);
  }
}
